import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { CONSENT_CATEGORIES, COOKIE_REGISTRY } from '@/constants/cookie-registry'
import consentToggle from './components/consent-toggle/index.vue'

const BANNER_CLASS = 'has-consent-banner'
const BANNER_HEIGHT_VAR = '--consent-banner-height'

const SCROLL_LOCK_CLASS = 'hide'

export default defineComponent({
  name: 'cookie-consent',
  components: {
    consentToggle,
  },
  data() {
    return {
      categories: CONSENT_CATEGORIES,
      bannerObserver: null,
    }
  },
  computed: {
    ...mapGetters({
      isConsentOpen: 'consent/isConsentOpen',
      isBannerVisible: 'consent/isBannerVisible',
      isSettingsVisible: 'consent/isSettingsVisible',
      draft: 'consent/consentDraft',
    }),

    cookiesByCategory() {
      return COOKIE_REGISTRY.reduce((acc, cookie) => {
        if (!acc[cookie.category]) acc[cookie.category] = []
        acc[cookie.category].push(cookie)
        return acc
      }, {})
    },
  },
  watch: {
    isSettingsVisible: {
      immediate: true,
      handler(visible) {
        this.toggleScrollLock(visible)
      },
    },
    isBannerVisible: {
      immediate: true,
      handler(visible) {
        this.toggleBannerClass(visible)
      },
    },
  },
  beforeDestroy() {
    this.toggleScrollLock(false)
    this.toggleBannerClass(false)
    this.stopBannerMeasure()
  },
  methods: {
    ...mapActions({
      acceptAll: 'consent/ACCEPT_ALL',
      rejectAll: 'consent/REJECT_ALL',
      saveCustom: 'consent/SAVE_CUSTOM',
      openSettings: 'consent/OPEN_SETTINGS',
      closeSettings: 'consent/CLOSE_SETTINGS',
    }),
    ...mapMutations({
      setDraftCategory: 'consent/SET_DRAFT_CATEGORY',
    }),

    htmlNode() {
      return document.getElementById('html')
    },

    toggleScrollLock(locked) {
      const node = this.htmlNode()
      if (!node) return
      node.classList[locked ? 'add' : 'remove'](SCROLL_LOCK_CLASS)
    },

    toggleBannerClass(visible) {
      const node = this.htmlNode()
      if (!node) return
      node.classList[visible ? 'add' : 'remove'](BANNER_CLASS)

      if (visible) {
        this.$nextTick(this.startBannerMeasure)
      } else {
        this.stopBannerMeasure()
      }
    },

    measureBanner() {
      const banner = this.$refs.banner
      const height = banner ? banner.offsetHeight : 0
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, height + 'px')
    },

    startBannerMeasure() {
      this.measureBanner()

      if (typeof ResizeObserver === 'function' && this.$refs.banner) {
        this.bannerObserver = new ResizeObserver(this.measureBanner)
        this.bannerObserver.observe(this.$refs.banner)
      } else {
        window.addEventListener('resize', this.measureBanner)
      }
    },

    stopBannerMeasure() {
      if (this.bannerObserver) {
        this.bannerObserver.disconnect()
        this.bannerObserver = null
      } else {
        window.removeEventListener('resize', this.measureBanner)
      }

      document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR)
    },

    isCategoryEnabled(category) {
      if (category.required) return true
      return Boolean(this.draft[category.key])
    },

    onToggleCategory(category, value) {
      if (category.required) return
      this.setDraftCategory({ key: category.key, value })
    },

    onAcceptAll(source) {
      this.acceptAll({ source })
    },

    onRejectAll(source) {
      this.rejectAll({ source })
    },

    onSaveCustom() {
      this.saveCustom({ source: 'settings' })
    },

    onOpenSettings(openedFrom) {
      this.openSettings({ openedFrom })
    },

    onCloseSettings() {
      this.closeSettings()
    },
  },
})
